import React, { FC, Fragment, useEffect, useState } from 'react';
import { Box, makeStyles, Typography, Grid } from '@material-ui/core';
import moment from 'moment-timezone';

import { OrderingBizHour, OrderingHoliday } from 'kiolyn-api';

import inBizHours from '../../../domain/usecases/CheckInWorkingHours';
import inHoliday from '../../../domain/usecases/CheckInWorkingHoursHoliday';

const useStyles = makeStyles(theme => ({
  horizontalContainer: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1)
  },
  horizontalStatus: {
    textAlign: 'right',
    fontWeight: 'bold',
    fontSize: theme.typography.fontSize * 1.25
  },
  horizontalBizHour: {
    ...theme.typography.caption,
    marginLeft: theme.spacing(1)
  },
  verticalStatus: {
    fontWeight: 'bold',
    textAlign: 'center',
    fontSize: theme.typography.fontSize * 1.5
  },
  verticalBizHourTitle: {
    textAlign: 'center',
    fontWeight: 'bold',
    marginTop: theme.spacing(1)
  },
  verticalBizHour: {
    textAlign: 'center'
  }
}));

interface BizHourProps {
  validatedDate?: Date;
  bizHours?: OrderingBizHour[];
  holidays?: OrderingHoliday[];
  variant?: 'textonly' | 'horizontal' | 'vertical';
  timezone?: string;
}

const formatBusinessHour = (data?: moment.Moment) => {
  if (!data) return undefined;
  return data.clone().format('hh:mm A');
};

const BizHour: FC<BizHourProps> = ({
  validatedDate,
  bizHours,
  holidays,
  variant,
  timezone
}) => {
  const classes = useStyles();

  const calculateState = (): {
    message: string;
    isWorking: boolean;
    bizHour?: any;
    holiday?: OrderingHoliday;
  } => {
    const [isWorkingHoliday, holiday] = inHoliday(
      holidays,
      validatedDate,
      timezone
    );
    const [isWorking, bizHour] = inBizHours(
      bizHours ?? [],
      validatedDate,
      timezone
    );

    let message = `Store is closed on ${moment(validatedDate).format('dddd')}`;
    if (holiday) {
      if (holiday.is_working) {
        message = `Store's bussiness hours on ${holiday.name}`;
        if (holiday.from_1 && holiday.to_1) {
          message += ` ${holiday.from_1}-${holiday.to_1}`;
        }
        if (holiday.from_2 && holiday.to_2) {
          message += `, ${holiday.from_2}-${holiday.to_2}`;
        }
      } else {
        message = `Store is closed on ${holiday.name}`;
      }
    } else if (bizHour) {
      message = `Store's business hours`;
      if (bizHour.from_1 && bizHour.to_1) {
        message += ` ${formatBusinessHour(bizHour.from_1)}-${formatBusinessHour(
          bizHour.to_1
        )}`;
      }
      if (bizHour.from_2 && bizHour.to_2) {
        message += `, ${formatBusinessHour(
          bizHour.from_2
        )}-${formatBusinessHour(bizHour.to_2)}`;
      }
    }

    return {
      message,
      isWorking: holiday ? isWorkingHoliday : isWorking,
      bizHour,
      holiday
    };
  };

  const [state, setState] = useState(calculateState());

  useEffect(() => {
    const interval = setInterval(() => {
      setState(Object.assign(state, calculateState()));
    }, 1000 * 60);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!bizHours && !holidays) {
    return null;
  }

  if (variant === 'horizontal') {
    return (
      <Grid container className={classes.horizontalContainer}>
        {((!state.holiday && state.bizHour) || state.holiday?.is_working) && (
          <Grid item xs={8}>
            <Typography variant="body1">WORKING HOURS</Typography>
            {state.holiday && (
              <Fragment>
                {state.holiday.from_1 && state.holiday.to_1 && (
                  <Typography className={classes.horizontalBizHour}>
                    {`* ${state.holiday.from_1} - ${state.holiday.to_1}`}
                  </Typography>
                )}
                {state.holiday.from_2 && state.holiday.to_2 && (
                  <Typography className={classes.horizontalBizHour}>
                    {`* ${state.holiday.from_2} - ${state.holiday.to_2}`}
                  </Typography>
                )}
              </Fragment>
            )}
            {!state.holiday && state.bizHour && (
              <Fragment>
                {state.bizHour.from_1 && state.bizHour.to_1 && (
                  <Typography className={classes.horizontalBizHour}>
                    {`* ${formatBusinessHour(
                      state.bizHour.from_1
                    )} - ${formatBusinessHour(state.bizHour.to_1)}`}
                  </Typography>
                )}
                {state.bizHour.from_2 && state.bizHour.to_2 && (
                  <Typography className={classes.horizontalBizHour}>
                    {`* ${formatBusinessHour(
                      state.bizHour.from_2
                    )} - ${formatBusinessHour(state.bizHour.to_2)}`}
                  </Typography>
                )}
              </Fragment>
            )}
          </Grid>
        )}
        <Grid item xs={4} container direction="column" justify="center">
          {state.isWorking ? (
            <Typography className={classes.horizontalStatus} color="primary">
              OPEN
            </Typography>
          ) : (
            <Typography className={classes.horizontalStatus} color="secondary">
              CLOSED
            </Typography>
          )}
        </Grid>
      </Grid>
    );
  }

  if (variant === 'vertical') {
    return (
      <Box>
        {state.isWorking ? (
          <Typography className={classes.verticalStatus} color="primary">
            OPEN
          </Typography>
        ) : (
          <Typography className={classes.verticalStatus} color="secondary">
            CLOSED
          </Typography>
        )}

        {((!state.holiday && state.bizHour) || state.holiday?.is_working) && (
          <Fragment>
            <Typography className={classes.verticalBizHourTitle}>
              WORKING HOURS
            </Typography>
            {state.holiday && (
              <Fragment>
                {state.holiday.from_1 && state.holiday.to_1 && (
                  <Typography className={classes.verticalBizHour}>
                    {`* ${state.holiday.from_1} - ${state.holiday.to_1}`}
                  </Typography>
                )}
                {state.holiday.from_2 && state.holiday.to_2 && (
                  <Typography className={classes.verticalBizHour}>
                    {`* ${state.holiday.from_2} - ${state.holiday.to_2}`}
                  </Typography>
                )}
              </Fragment>
            )}
            {!state.holiday && state.bizHour && (
              <Fragment>
                {state.bizHour.from_1 && state.bizHour.to_1 && (
                  <Typography className={classes.verticalBizHour}>
                    {`* ${formatBusinessHour(
                      state.bizHour.from_1
                    )} - ${formatBusinessHour(state.bizHour.to_1)}`}
                  </Typography>
                )}
                {state.bizHour.from_2 && state.bizHour.to_2 && (
                  <Typography className={classes.verticalBizHour}>
                    {`* ${formatBusinessHour(
                      state.bizHour.from_2
                    )} - ${formatBusinessHour(state.bizHour.to_2)}`}
                  </Typography>
                )}
              </Fragment>
            )}
          </Fragment>
        )}
      </Box>
    );
  }

  return <Typography display="block">* {state.message}</Typography>;
};

export default BizHour;
